import type { QueryKey } from "@tanstack/react-query";
import { useListMutate, type ListMutate } from "@shared/hooks/useListMutate";
import { useQueryData, type UseQueryDataResult } from "@src/hooks/useQueryData";

/**
 * Параметры хука {@link useBusinessListQuery}.
 * @template TData тип элемента списка
 * @template TFilter тип фильтра
 */
export interface UseBusinessListQueryOptions<TData, TFilter extends object> {
    /**
     * Ключ запроса. Строка → `[queryKey, filter, ...extraQueryKey]`; массив — как есть.
     */
    queryKey: string | readonly unknown[];
    /**
     * Загрузка списка. Вызывается только когда запрос enabled; `filter` не `null`.
     * @param filter текущий фильтр страницы
     * @param signal сигнал отмены
     */
    queryFn: (filter: TFilter, signal: AbortSignal) => Promise<TData[]>;
    /** Id элемента для `list.mutate`. */
    getId: (item: TData) => string | number;
    /** Фильтр страницы (URL-agnostic: передаётся снаружи). */
    filter: TFilter | null;
    /** Подмена дефолта `enabled: filter != null`. */
    enabled?: boolean | ((filter: TFilter | null) => boolean);
    /** Доп. сегменты ключа, если `queryKey` — строка. */
    extraQueryKey?: readonly unknown[];
}

/**
 * Результат {@link useBusinessListQuery}.
 * @template TData тип элемента списка
 * @template TFilter тип фильтра
 */
export interface UseBusinessListQueryResult<TData, TFilter extends object> {
    data: TData[];
    isFetching: boolean;
    filter: TFilter | null;
    refetch: () => Promise<unknown>;
    setData: UseQueryDataResult<TData[]>["setData"];
    getData: UseQueryDataResult<TData[]>["getData"];
    getDataForKey: UseQueryDataResult<TData[]>["getDataForKey"];
    cancelQueries: UseQueryDataResult<TData[]>["cancelQueries"];
    queryKey: QueryKey;
    mutate: ListMutate<TData>;
}

/**
 * Связка списковой страницы: `useQueryData` + `useListMutate` для обновления кэша после мутаций.
 *
 * @template TData тип элемента списка
 * @template TFilter тип фильтра
 */
export function useBusinessListQuery<TData, TFilter extends object = Record<string, unknown>>(
    options: UseBusinessListQueryOptions<TData, TFilter>,
): UseBusinessListQueryResult<TData, TFilter> {
    const { queryKey, queryFn, getId, enabled: enabledOption, extraQueryKey, filter } = options;
    const defaultQueryEnabled = filter != null;

    const resolvedQueryKey = typeof queryKey === "string" ? [queryKey, filter, ...(extraQueryKey ?? [])] : queryKey;

    const query = useQueryData<TData[]>({
        queryKey: resolvedQueryKey,
        queryFn: async ({ signal }) => queryFn(filter!, signal),
        enabled:
            enabledOption != null
                ? typeof enabledOption === "function"
                    ? enabledOption(filter)
                    : enabledOption
                : defaultQueryEnabled,
    });

    const mutate = useListMutate(query, getId);

    return {
        data: query.data ?? [],
        isFetching: query.isFetching,
        filter,
        refetch: query.refetch,
        setData: query.setData,
        getData: query.getData,
        getDataForKey: query.getDataForKey,
        cancelQueries: query.cancelQueries,
        queryKey: resolvedQueryKey,
        mutate,
    };
}
